import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabState from '../hooks/useTabState';

import { BASE, authFetch, titleCase } from '../constants';
import AgentLearningTab from '../components/AgentLearningTab';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

const GOV_API = `${BASE}/api/v1/ai-governance`;
const AUTO_API = `${BASE}/api/v1/auto-approve`;
const CONTROLS_API = `${BASE}/api/v1/controls`;

// -- Types --
interface MatrixLevel { level: string; role: string; auto_approve_limit: number | null; confidence_minimum?: number; escalation_trigger: string; }
interface MatrixData { levels: MatrixLevel[]; blocked_types: string[]; force_escalate_rules: { condition: string; target: string }[]; force_escalated_count: number; thresholds: { auto_resolve: number; cfo_escalation: number; confidence_minimum: number }; }

interface AuditCheck { check_name: string; description: string; total_checked: number; passed: number; pass_rate: number; findings: { CRITICAL: number; HIGH: number; WARNING: number; INFO: number }; }
interface AuditData { total_audited: number; checks: AuditCheck[]; overall_critical_count: number; overall_pass_rate: number; }

interface FunnelGate { gate: number; name: string; description: string; blocked: number; remaining: number; passed?: number; }
interface FunnelData { total_exceptions: number; gates: FunnelGate[]; auto_resolved: number; human_resolved: number; resolve_rate_of_eligible: number; remaining_for_human: number; mode_1_resolved?: number; mode_2_resolved?: number; resolution_stages?: { mode_1?: { count: number; label: string }; mode_2?: { count: number; label: string }; failed?: { count: number; label: string } }; resolution_summary?: { total_resolved: number; pending_review: number; resolve_rate: number }; status_breakdown?: Record<string, number>; }

interface SilBucket { range: string; label: string; count: number; percentage: number; color: string; }
interface SignalDef { signal: string; weight: number; description: string; }
interface SilData { total_with_scores: number; distribution: SilBucket[]; mean_sil: number; median_sil: number; auto_resolve_threshold: number; above_threshold_count: number; above_threshold_percentage: number; signal_definitions: SignalDef[]; calibration_note?: string; batch_data_note?: string; }

interface HealthData { critical_findings: number; auto_resolve_gate6_pass_rate: number; total_audit_trail_entries: number; segregation_violations: number; ungrounded_amounts_blocked: number; total_exceptions_processed: number; total_resolved: number; total_audited_resolutions: number; resolution_methods: { auto_resolved: number; human_resolved: number; escalated: number; under_investigation: number }; balance_validation: { total_jes_validated: number; rejected_imbalanced: number; rejection_rate: number }; }

interface CalibrationEntry { exception_type: string; confidence_band: string; total: number; accepted: number; modified: number; rejected: number; actual_accuracy: number; calibration_error: number; }
interface CalibrationData { status?: string; message?: string; calibrations?: CalibrationEntry[]; bands?: CalibrationEntry[]; }

interface OutcomesData { status?: string; total_outcomes?: number; total?: number; accepted?: number; modified?: number; rejected?: number; escalated?: number; acceptance_rate?: number | null; learning_data_points?: number; message?: string; }

interface SilCalibrationState { maturity_state: string; resolved_count: number; blend_weight: number; current_weights: Record<string, number>; industry_baselines: Record<string, Record<string, number>>; maturity_thresholds: { cold_start: number; mature: number }; }

interface AutoApprovalDashboard { total_processed: number; approved: number; blocked: number; routed_to_human: number; blocked_by_layer: Record<string, number>; layer_labels: Record<string, string>; drift_alerts: { exception_id: number; account_family: string; cumulative_adjustment: number; tb_total: number; pct: number; threshold: number }[]; provisional_pending: number; }
interface CoverageData { total_exception_types: number; mapped: number; unmapped: number; unmapped_types: string[]; coverage_pct: number; }

// -- Helpers --
function fmt$(n: number | null): string {
  if (n === null || n === undefined) return 'Unlimited';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtN(n: number): string { return n.toLocaleString(); }

function passColor(rate: number): string {
  if (rate >= 99) return 'var(--positive)';
  if (rate >= 95) return '#eab308';
  return '#ef4444';
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 20, ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>{children}</h2>;
}

// -- Methodology Header Component --
function MethodologyHeader({ what, why, how }: { what: string; why: string; how: string }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div style={{
      marginBottom: 20, padding: '14px 18px',
      background: 'var(--bg-card)', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      border: '1px solid var(--accent-border)',
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)', textTransform: 'uppercase', letterSpacing: 1 }}>Methodology</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>{expanded ? '\u25B2' : '\u25BC'}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: 'var(--navy-800)' }}>What it measures: </span>{what}
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: 'var(--navy-800)' }}>Why it matters: </span>{why}
          </div>
          <div>
            <span style={{ fontWeight: 700, color: 'var(--navy-800)' }}>How it works: </span>{how}
          </div>
        </div>
      )}
    </div>
  );
}

// -- Tab definitions --
const TABS = [
  'Materiality & Approval',
  'System Health',
  'Controls Library',
  'Resolution Auditor',
  'Resolution Pipeline',
  'SIL Framework',
  'Safety Controls',
  'Feedback Loop',
  'Confidence Calibration',
  'Auto-Approval Engine',
  'Agent Learning',
] as const;
type Tab = typeof TABS[number];

// -- Methodology content --
const METHODOLOGY: Record<Tab, { what: string; why: string; how: string }> = {
  'Materiality & Approval': {
    what: 'Dollar thresholds that determine approval routing by role level.',
    why: 'SOX 302/404 requires segregation of approval authority based on financial materiality. Ensures no single individual can approve transactions above their authorization level.',
    how: 'Exceptions are routed based on amount to the appropriate approval level. L1 (AI Auto) resolves under $50K. L2-L5 handle progressively larger amounts. L6 (CFO) approves above $5M. L7 (Board) above $50M. Blocked types (fraud, SOX violations) always escalate regardless of amount.',
  },
  'System Health': {
    what: 'Real-time platform operational metrics across all AI processing.',
    why: 'Continuous monitoring ensures AI decisions meet quality standards before any financial posting occurs. Provides instant visibility into platform reliability.',
    how: 'Aggregates critical findings count, 6-gate pass rate, audit trail completeness, resolution counts by mode (AI-processed vs human-resolved vs escalated), and balance validation status from live production data.',
  },
  'Controls Library': {
    what: 'Maps every exception type to its governing accounting standard and control framework.',
    why: 'External auditors require evidence that each exception type has a documented control response with specific standard references (ASC, IFRS, COSO, SOX).',
    how: 'Each of the platform\'s exception types is mapped to GAAP/IFRS paragraph references, COSO framework components, and SOX control assertions. Coverage percentage tracks how many types have complete mappings vs gaps.',
  },
  'Resolution Auditor': {
    what: 'Six independent validation checks every AI-generated resolution must pass.',
    why: 'Ensures no AI resolution can post a journal entry that violates fundamental accounting rules -- balanced entries, grounded amounts, proper authorization.',
    how: 'Each check runs independently: (1) Amount Grounding -- JE amounts must trace to source ERP data, (2) No Invented Rates -- AI cannot fabricate tax rates, FX rates, or percentages, (3) Balance Validation -- debits must equal credits, (4) Critical Findings Block -- any CRITICAL audit finding prevents posting, (5) Full Audit Trail -- SOX 404 immutable record for every decision, (6) Segregation of Duties -- the AI that detects cannot also approve.',
  },
  'Resolution Pipeline': {
    what: 'The end-to-end flow showing how exceptions are detected, resolved, and approved.',
    why: 'Complete transparency into AI decision-making. The CFO sees exactly how each exception moves through the system -- from detection to resolution to approval or escalation.',
    how: 'Mode 1 (Template Resolver) generates structural resolutions in ~8ms using pre-built templates. Mode 2 (LLM) enriches with specific diagnosis, account-level analysis, and reference citations. All resolutions then pass through the 6-gate approval pipeline. Gates evaluate blocked types, escalation level, materiality ($50K threshold), SIL confidence (>=85%), risk matrix, and audit findings.',
  },
  'SIL Framework': {
    what: '7-signal composite score measuring AI confidence in each resolution.',
    why: 'Only resolutions scoring >=85% are eligible for auto-approval. Prevents overconfident AI decisions when data is incomplete or patterns are unfamiliar.',
    how: 'Weighted blend of 7 independent signals -- Data Completeness (15%), Document Matching (20%), Entity Intelligence (15%), Rules Compliance (10%), Resolution Learning (15%), Historical Patterns (15%), Cross-Doc Verification (10%). During cold start (< 200 resolutions), blends actual scores with industry baseline to prevent overconfidence on sparse data.',
  },
  'Safety Controls': {
    what: '6 mandatory checks that every AI decision passes through before any posting or resolution occurs.',
    why: 'Hard guardrails that prevent the AI from making fundamentally wrong financial decisions. These cannot be overridden or bypassed.',
    how: 'Each control operates independently and must show green (OK) for the platform to function normally. Amount Grounding: JE amounts trace to source data. No Invented Rates: AI cannot fabricate rates or percentages. Balance Validation: debits = credits. Critical Findings Block: CRITICAL findings prevent auto-resolution. Full Audit Trail: SOX 404-compliant immutable record. Segregation of Duties: detection agent and approval agent are independent.',
  },
  'Feedback Loop': {
    what: 'Tracks how humans respond to AI-generated resolutions and feeds decisions back into the learning system.',
    why: 'The platform gets smarter over time. Every human accept, reject, or modify teaches the AI what works and what doesn\'t for each exception type.',
    how: 'When a human reviews an AI resolution, their decision (accepted, rejected, modified) is captured with details. Acceptance rates per exception type drive Mode 1 promotion. Modification patterns identify where AI resolutions need improvement. After 3+ accepted outcomes, the system auto-generates evaluation cases for continuous quality testing.',
  },
  'Confidence Calibration': {
    what: 'Compares AI-predicted confidence bands against actual human acceptance rates.',
    why: 'Detects systematic bias in AI confidence scoring. An overconfident AI auto-approves items that humans would reject. An underconfident AI forces unnecessary human review.',
    how: 'Groups resolutions into confidence bands (0.00-0.49, 0.50-0.69, 0.70-0.84, 0.85-0.94, 0.95-1.00). For each band, compares the AI\'s predicted confidence against the actual human acceptance rate. Positive calibration error = AI overconfident. Negative = AI underconfident. Calibration improves as more human decisions accumulate.',
  },
  'Auto-Approval Engine': {
    what: 'The complete gate sequence that determines whether a resolved exception is auto-posted or requires human approval.',
    why: 'SOX-compliant approval workflow that enforces materiality limits, risk thresholds, and regulatory requirements without manual routing decisions.',
    how: '7 sequential layers, each configured via ThresholdConfig (no hardcoded values): L1 Type Check (blocked types escalate), L2 Escalation (pre-assigned levels), L3 Materiality (default $50K), L4 SIL Confidence (default 85%), L5 Risk Matrix (5-factor score), L6 Regulatory (jurisdiction checks), L7 Provisional (time-limited auto-approval with confirmation deadline and drift monitoring). All thresholds configurable per client, entity, and exception type.',
  },
  'Agent Learning': {
    what: 'Tracks agent self-reflections, adaptation proposals, and durable insights extracted from conversations with users.',
    why: 'Agents improve over time by learning from decisions, user feedback, and recurring patterns. This tab provides transparency and human oversight over what agents learn.',
    how: 'Meta-agents generate reflections during diagnostic cycles. Conversation learnings are extracted via Claude when chats close. All adaptations require human approval before taking effect.',
  },
};

// -- Main component --
export default function AIGovernance() {
  const [matrix, setMatrix] = useState<MatrixData | null>(null);
  const [audit, setAudit] = useState<AuditData | null>(null);
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [sil, setSil] = useState<SilData | null>(null);
  const [health, setHealth] = useState<HealthData | null>(null);
  const [calibration, setCalibration] = useState<CalibrationData | null>(null);
  const [outcomes, setOutcomes] = useState<OutcomesData | null>(null);
  const [silCal, setSilCal] = useState<SilCalibrationState | null>(null);
  const [autoApproval, setAutoApproval] = useState<AutoApprovalDashboard | null>(null);
  const [coverage, setCoverage] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useTabState<Tab>('tab', TABS, 'Materiality & Approval');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, aRes, fRes, sRes, hRes, cRes, oRes, scRes, aaRes, covRes] = await Promise.all([
        authFetch(`${GOV_API}/materiality-matrix`),
        authFetch(`${GOV_API}/audit-summary`),
        authFetch(`${GOV_API}/resolution-pipeline-funnel`),
        authFetch(`${GOV_API}/sil-distribution`),
        authFetch(`${GOV_API}/system-health`),
        authFetch(`${GOV_API}/confidence-calibration`),
        authFetch(`${GOV_API}/resolution-outcomes-summary`),
        authFetch(`${GOV_API}/sil-calibration-state`),
        authFetch(`${AUTO_API}/dashboard-summary`),
        authFetch(`${CONTROLS_API}/coverage`),
      ]);
      setMatrix(await mRes.json());
      setAudit(await aRes.json());
      setFunnel(await fRes.json());
      setSil(await sRes.json());
      setHealth(await hRes.json());
      setCalibration(await cRes.json());
      setOutcomes(await oRes.json());
      setSilCal(await scRes.json());
      setAutoApproval(await aaRes.json());
      if (covRes.ok) setCoverage(await covRes.json());
    } catch (e) {
      console.error('Failed to load governance data', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dim)' }}>Loading AI Governance data...</div>
  );

  // Table column definitions
  const matrixCols: Column[] = [
    { key: 'level', label: 'Level', render: (v: string) => <span style={{ fontWeight: 700, color: levelColor(v) }}>{v}</span> },
    { key: 'role', label: 'Role' },
    { key: 'auto_approve_limit', label: 'Approval Limit', align: 'right', render: (v: number | null) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt$(v)}</span> },
    { key: 'escalation_trigger', label: 'Escalation Trigger', render: (v: string) => <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{v}</span> },
  ];

  const auditCols: Column[] = [
    { key: 'check_name', label: 'Check', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'description', label: 'Description', render: (v: string) => <span style={{ color: 'var(--text-dim)' }}>{v}</span> },
    { key: 'pass_rate', label: 'Pass Rate', align: 'center', width: 90, render: (v: number) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
        <div style={{ width: 50, height: 6, background: 'rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(v, 100)}%`, height: '100%', background: passColor(v), borderRadius: 3 }} />
        </div>
        <span style={{ color: passColor(v), fontWeight: 600, fontSize: 11 }}>{v}%</span>
      </div>
    )},
    { key: 'critical', label: 'CRIT', align: 'center', width: 60, render: (_: any, row: AuditCheck) => <span style={{ color: row.findings.CRITICAL > 0 ? '#ef4444' : 'var(--positive)', fontWeight: 600 }}>{row.findings.CRITICAL}</span> },
    { key: 'high', label: 'HIGH', align: 'center', width: 60, render: (_: any, row: AuditCheck) => <span style={{ color: row.findings.HIGH > 0 ? '#f59e0b' : 'var(--text-dim)' }}>{row.findings.HIGH}</span> },
    { key: 'warning', label: 'WARN', align: 'center', width: 60, render: (_: any, row: AuditCheck) => <span style={{ color: row.findings.WARNING > 0 ? '#eab308' : 'var(--text-dim)' }}>{row.findings.WARNING}</span> },
    { key: 'info', label: 'INFO', align: 'center', width: 60, render: (_: any, row: AuditCheck) => <span style={{ color: 'var(--text-dim)' }}>{row.findings.INFO}</span> },
  ];

  const calibrationCols: Column[] = [
    { key: 'exception_type', label: 'Exception Type', render: (v: string) => <span style={{ color: 'var(--navy-800)', fontSize: 11 }}>{titleCase(v)}</span> },
    { key: 'confidence_band', label: 'Band', render: (v: string) => <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11 }}>{v}</span> },
    { key: 'total', label: 'Total', align: 'center' },
    { key: 'actual_accuracy', label: 'Actual', align: 'center', render: (v: number) => <span style={{ fontWeight: 600 }}>{((v ?? 0) * 100).toFixed(0)}%</span> },
    { key: 'calibration_error', label: 'Status', align: 'center', render: (v: number) => {
      const absErr = Math.abs(v);
      const statusColor = absErr < 0.05 ? 'var(--positive)' : absErr < 0.15 ? '#eab308' : '#ef4444';
      const statusLabel = absErr < 0.05 ? 'Accurate' : v > 0 ? 'Overconfident' : 'Underconfident';
      return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${statusColor}20`, color: statusColor }}>{statusLabel}</span>;
    }},
  ];

  return (
    <PageLayout
      title="AI Governance & Controls"
      subtitle="SOX-grade transparency into every AI decision. Every number on this page is computed from live data."
    >
      <TabBar
        tabs={TABS.map(t => ({ key: t, label: t }))}
        active={activeTab}
        onChange={v => setActiveTab(v as Tab)}
      />

      {/* Methodology header for active tab */}
      <MethodologyHeader {...METHODOLOGY[activeTab]} />

      {/* TAB 1: Materiality & Approval */}
      {activeTab === 'Materiality & Approval' && (
        <Card>
          <SectionTitle>Materiality &amp; Approval Authority</SectionTitle>
          <DataTable columns={matrixCols} rows={matrix?.levels || []} rowKey="level" />
          <div style={{ marginTop: 16, padding: '10px 12px', background: 'var(--negative-bg)', borderRadius: 6, border: '1px solid var(--negative-border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--negative)', marginBottom: 6 }}>Always Blocked from Auto-Resolve</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(matrix?.blocked_types || []).map(t => (
                <span key={t} style={{ padding: '2px 8px', background: '#fef2f2', borderRadius: 4, fontSize: 11, color: 'var(--negative)' }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--accent-subtle)', borderRadius: 6, border: '1px solid var(--accent-border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 6 }}>Force-Escalation Rules ({matrix?.force_escalated_count} active)</div>
            {(matrix?.force_escalate_rules || []).map((r, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 2 }}>
                <span style={{ color: 'var(--navy-800)' }}>{r.condition}</span> {'->'} <span style={{ color: 'var(--warning)' }}>{r.target}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 2: System Health */}
      {activeTab === 'System Health' && (
        <Card>
          <SectionTitle>System Health &amp; Live Stats</SectionTitle>
          <KpiGrid columns={3} items={[
            { label: 'CRITICAL Findings', value: health?.critical_findings ?? '-', color: health?.critical_findings === 0 ? 'var(--positive)' : '#ef4444' },
            { label: 'Gate 6 Pass Rate', value: `${health?.auto_resolve_gate6_pass_rate ?? 0}%`, color: passColor(health?.auto_resolve_gate6_pass_rate ?? 0) },
            { label: 'Audit Trail Entries', value: fmtN(health?.total_audit_trail_entries ?? 0) },
          ]} />
          <KpiGrid columns={3} items={[
            { label: 'AI-Processed', value: fmtN(health?.resolution_methods?.auto_resolved ?? 0), color: 'var(--positive)' },
            { label: 'Human-Resolved', value: fmtN(health?.resolution_methods?.human_resolved ?? 0), color: 'var(--navy-700)' },
            { label: 'Escalated', value: fmtN(health?.resolution_methods?.escalated ?? 0), color: '#f59e0b' },
          ]} />
          <div style={{ padding: '12px', background: 'rgba(34,197,94,0.06)', borderRadius: 6, border: '1px solid #f0fdf4', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--positive)', marginBottom: 8 }}>Balance Validation Gate</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <span style={{ color: 'var(--text-dim)' }}>JEs Balanced</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmtN(health?.balance_validation?.total_jes_validated ?? 0)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
              <span style={{ color: 'var(--text-dim)' }}>Rejected (Imbalanced)</span>
              <span style={{ color: health?.balance_validation?.rejected_imbalanced === 0 ? 'var(--positive)' : '#ef4444', fontWeight: 600 }}>{health?.balance_validation?.rejected_imbalanced ?? 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
              <span style={{ color: 'var(--text-dim)' }}>Rejection Rate</span>
              <span style={{ color: 'var(--positive)', fontWeight: 600 }}>{health?.balance_validation?.rejection_rate ?? 0}%</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-dim)' }}>
            <span>Total Processed: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmtN(health?.total_exceptions_processed ?? 0)}</span></span>
            <span>Audited Resolutions: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmtN(health?.total_audited_resolutions ?? 0)}</span></span>
          </div>
        </Card>
      )}

      {/* TAB 3: Controls Library */}
      {activeTab === 'Controls Library' && coverage && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SectionTitle>Controls Library Coverage</SectionTitle>
              <span style={{ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: coverage.coverage_pct === 100 ? '#f0fdf4' : 'rgba(251,191,36,0.15)', color: coverage.coverage_pct === 100 ? 'var(--positive)' : 'var(--warning)' }}>
                {coverage.coverage_pct}%
              </span>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{coverage.mapped}/{coverage.total_exception_types} exception types mapped</span>
          </div>
          <div style={{ width: '100%', height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden', marginTop: 4 }}>
            <div style={{ width: `${coverage.coverage_pct}%`, height: '100%', borderRadius: 4, background: coverage.coverage_pct === 100 ? 'var(--positive)' : 'var(--warning)', transition: 'width 0.5s' }} />
          </div>
          {coverage.unmapped > 0 && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(251,191,36,0.06)', borderRadius: 6, border: '1px solid rgba(251,191,36,0.15)' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--warning)', marginBottom: 4 }}>{coverage.unmapped} unmapped</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(coverage.unmapped_types || []).map(t => (
                  <span key={t} style={{ padding: '2px 8px', background: 'rgba(251,191,36,0.1)', borderRadius: 4, fontSize: 10, color: '#fcd34d' }}>{titleCase(t)}</span>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* TAB 4: Resolution Auditor */}
      {activeTab === 'Resolution Auditor' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionTitle>Resolution Auditor &mdash; 6-Point Framework</SectionTitle>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{audit?.total_audited ?? 0} resolutions audited</span>
              <span style={{ padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: !audit ? 'rgba(156,163,175,0.15)' : (audit.overall_critical_count ?? 0) === 0 ? '#f0fdf4' : '#fef2f2', color: !audit ? '#9ca3af' : (audit.overall_critical_count ?? 0) === 0 ? 'var(--positive)' : '#ef4444' }}>
                {!audit ? 'NOT AUDITED' : `${audit.overall_critical_count ?? 0} CRITICAL`}
              </span>
            </div>
          </div>
          <DataTable columns={auditCols} rows={audit?.checks || []} rowKey="check_name" />
        </Card>
      )}

      {/* TAB 5: Resolution Pipeline */}
      {activeTab === 'Resolution Pipeline' && (
        <Card>
          <SectionTitle>Resolution Pipeline</SectionTitle>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16 }}>
            {fmtN(funnel?.total_exceptions ?? 0)} total exceptions through Mode 1 / Mode 2 resolution
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ height: 28, background: 'var(--accent-subtle)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-700)' }}>{fmtN(funnel?.total_exceptions ?? 0)} Total Exceptions</span>
            </div>
            {(() => {
              const m1 = Math.max(funnel?.resolution_stages?.mode_1?.count ?? funnel?.mode_1_resolved ?? 0, 0);
              const m2 = Math.max(funnel?.resolution_stages?.mode_2?.count ?? funnel?.mode_2_resolved ?? 0, 0);
              const total = Math.max(funnel?.total_exceptions ?? 1, 1);
              return (<>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 60, fontSize: 10, fontWeight: 700, color: 'var(--positive)', textAlign: 'right' }}>Mode 1</div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div style={{ width: '100%', height: 26, background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min((m1/total)*100, 100)}%`, height: '100%', background: 'rgba(34,197,94,0.25)', borderRadius: 4 }} />
                    </div>
                    <div style={{ position: 'absolute', top: 0, left: 8, right: 8, height: 26, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'var(--text-primary)' }}>Template Resolver</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--positive)' }}>{fmtN(m1)}</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 60, fontSize: 10, fontWeight: 700, color: 'var(--navy-800)', textAlign: 'right' }}>Mode 2</div>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <div style={{ width: '100%', height: 26, background: 'rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min((m2/total)*100, 100)}%`, height: '100%', background: 'rgba(167,139,250,0.25)', borderRadius: 4 }} />
                    </div>
                    <div style={{ position: 'absolute', top: 0, left: 8, right: 8, height: 26, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'var(--text-primary)' }}>LLM Resolution</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)' }}>{fmtN(m2)}</span>
                    </div>
                  </div>
                </div>
              </>);
            })()}
            {(funnel?.gates || []).length > 0 && (
              <div style={{ marginTop: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Auto-Approval Gates ({fmtN(funnel?.total_exceptions ?? 0)} evaluated)
                </div>
                {(funnel?.gates || []).map((g: any) => {
                  const passed = Math.max(g.passed ?? 0, 0);
                  const blocked = Math.max(g.blocked ?? 0, 0);
                  const gateTotal = passed + blocked;
                  const passedPct = gateTotal > 0 ? Math.min(Math.max((passed / gateTotal) * 100, 0), 100) : 100;
                  const blockedPct = Math.min(100 - passedPct, 100);
                  return (
                    <div key={g.gate} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <div style={{ width: 18, fontSize: 9, fontWeight: 700, color: 'var(--navy-600)', textAlign: 'center' }}>G{g.gate}</div>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <div style={{ width: '100%', height: 20, background: 'rgba(0,0,0,0.04)', borderRadius: 3, overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${passedPct}%`, height: '100%', background: 'rgba(34,197,94,0.2)' }} />
                          {blocked > 0 && <div style={{ width: `${blockedPct}%`, height: '100%', background: '#fef2f2' }} />}
                        </div>
                        <div style={{ position: 'absolute', top: 0, left: 6, right: 6, height: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{g.name}</span>
                          <span style={{ fontSize: 9 }}>
                            {blocked > 0 && <span style={{ color: 'var(--negative)', marginRight: 4 }}>-{fmtN(blocked)}</span>}
                            <span style={{ color: 'var(--positive)' }}>{fmtN(passed)}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <KpiGrid columns={3} items={[
              { label: 'Total Resolved', value: fmtN(funnel?.resolution_summary?.total_resolved ?? funnel?.auto_resolved ?? 0), color: 'var(--positive)' },
              { label: 'Resolve Rate', value: `${Math.min(funnel?.resolution_summary?.resolve_rate ?? 0, 100)}%`, color: 'var(--navy-700)' },
              { label: 'Pending Review', value: fmtN(funnel?.resolution_summary?.pending_review ?? 0), color: 'var(--warning)' },
            ]} />
          </div>
        </Card>
      )}

      {/* TAB 6: SIL Framework */}
      {activeTab === 'SIL Framework' && (
        <Card>
          <SectionTitle>SIL Confidence Framework</SectionTitle>
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 16, lineHeight: 1.5 }}>
            {sil?.total_with_scores ?? 0} exceptions scored. Only {'>'} {((sil?.auto_resolve_threshold ?? 0.85) * 100).toFixed(0)}% eligible for auto-resolve.
            {silCal && (
              <span style={{ display: 'block', marginTop: 4, color: silCal.maturity_state === 'MATURE' ? 'var(--positive)' : '#fcd34d', fontStyle: 'italic' }}>
                {silCal.maturity_state} calibration -- {((silCal.blend_weight ?? 0) * 100).toFixed(0)}% actual, {((1 - (silCal.blend_weight ?? 0)) * 100).toFixed(0)}% industry baseline.
              </span>
            )}
          </p>
          <div style={{ marginBottom: 16 }}>
            {(sil?.distribution || []).map(b => {
              const maxPct = Math.max(...(sil?.distribution?.map(d => d.percentage) || [1]));
              const barWidth = maxPct > 0 ? (b.percentage / maxPct) * 100 : 0;
              return (
                <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 90, fontSize: 10, color: 'var(--text-dim)', textAlign: 'right' }}>{b.range}</div>
                  <div style={{ flex: 1, height: 20, background: 'rgba(0,0,0,0.04)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${barWidth}%`, height: '100%', background: b.color, borderRadius: 3, opacity: 0.7, transition: 'width 0.5s' }} />
                  </div>
                  <div style={{ width: 50, fontSize: 10, fontWeight: 600, color: b.color, textAlign: 'right' }}>{fmtN(b.count)}</div>
                  <div style={{ width: 40, fontSize: 10, color: 'var(--text-dim)', textAlign: 'right' }}>{b.percentage}%</div>
                </div>
              );
            })}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '6px 0' }}>
              <div style={{ width: 90, fontSize: 10, color: 'var(--positive)', textAlign: 'right', fontWeight: 600 }}>Threshold</div>
              <div style={{ flex: 1, borderTop: '2px dashed rgba(34,197,94,0.5)' }} />
              <div style={{ fontSize: 10, color: 'var(--positive)', fontWeight: 600 }}>{((sil?.auto_resolve_threshold ?? 0.85) * 100).toFixed(0)}%</div>
            </div>
          </div>
          <KpiGrid columns={3} items={[
            { label: 'Mean SIL', value: `${((sil?.mean_sil ?? 0) * 100).toFixed(1)}%` },
            { label: 'Median SIL', value: `${((sil?.median_sil ?? 0) * 100).toFixed(1)}%` },
            { label: 'Above Threshold', value: `${sil?.above_threshold_percentage ?? 0}%`, color: 'var(--positive)' },
          ]} />
          {silCal && (
            <div style={{ padding: 14, background: 'var(--accent-subtle)', borderRadius: 8, border: '1px solid var(--accent-border)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-800)', textTransform: 'uppercase', letterSpacing: 0.5 }}>SIL Maturity State</div>
                <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: silCal.maturity_state === 'MATURE' ? '#f0fdf4' : silCal.maturity_state === 'WARMING' ? 'rgba(251,191,36,0.15)' : 'var(--accent-subtle)', color: silCal.maturity_state === 'MATURE' ? 'var(--positive)' : silCal.maturity_state === 'WARMING' ? 'var(--warning)' : 'var(--navy-600)' }}>
                  {silCal.maturity_state}
                </span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)', marginBottom: 4 }}>
                  <span>{silCal.resolved_count} resolutions</span>
                  <span>{silCal.maturity_thresholds.mature} needed for mature</span>
                </div>
                <div style={{ width: '100%', height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min((silCal.resolved_count / silCal.maturity_thresholds.mature) * 100, 100)}%`, height: '100%', borderRadius: 4, transition: 'width 0.5s', background: silCal.maturity_state === 'MATURE' ? 'var(--positive)' : silCal.maturity_state === 'WARMING' ? 'var(--warning)' : 'var(--navy-600)' }} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 10 }}>
                Blend: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{(silCal.blend_weight * 100).toFixed(0)}%</span> actual, <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{((1 - silCal.blend_weight) * 100).toFixed(0)}%</span> baseline
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--navy-800)' }}>Current Module Weights</div>
                {Object.entries(silCal?.current_weights || {}).map(([mod, wt]) => {
                  const baseWeight: Record<string, number> = { data_completeness: 0.15, document_matching: 0.20, entity_intelligence: 0.15, rules_compliance: 0.10, resolution_learning: 0.15, historical_patterns: 0.15, cross_doc_verification: 0.10 };
                  const bw = baseWeight[mod] || 0.10;
                  return (
                    <div key={mod} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ minWidth: 130, color: 'var(--navy-800)' }}>{titleCase(mod)}</span>
                      <div style={{ flex: 1, height: 6, background: 'rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${wt * 100 * 5}%`, height: '100%', borderRadius: 3, background: wt > bw + 0.005 ? 'var(--navy-600)' : wt < bw - 0.005 ? 'var(--warning)' : 'var(--navy-800)' }} />
                      </div>
                      <span style={{ minWidth: 50, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{(wt * 100).toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            <div style={{ fontWeight: 700, color: 'var(--navy-800)', marginBottom: 6, fontSize: 11 }}>7 Independent Signals</div>
            {(sil?.signal_definitions || []).map(s => (
              <div key={s.signal} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                <span style={{ color: 'var(--navy-800)', minWidth: 140 }}>{titleCase(s.signal)} ({(s.weight * 100).toFixed(0)}%)</span>
                <span>{s.description}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 7: Safety Controls */}
      {activeTab === 'Safety Controls' && (
        <Card>
          <SectionTitle>AI Safety Controls</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>Every AI decision passes through these mandatory controls.</div>
              {safetyChecks.map((check, i) => {
                const passed = getCheckStatus(check.key, health);
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: passed ? '#f0fdf4' : '#fef2f2', color: passed ? 'var(--positive)' : '#ef4444', border: `1px solid ${passed ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
                      {passed ? 'OK' : '!'}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{check.label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>{check.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div>
              <div style={{ padding: 16, background: 'var(--accent-subtle)', borderRadius: 8, border: '1px solid var(--accent-border)', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-800)', marginBottom: 8 }}>Why Trust Agent Workforce?</div>
                <p style={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0 }}>
                  Agent Workforce employs a defense-in-depth approach to AI safety. Every AI-generated resolution passes through a
                  6-gate auto-resolve pipeline, a 6-point audit framework, and mandatory balance validation before any
                  journal entry is posted.
                </p>
              </div>
              <KpiGrid columns={2} items={[
                { label: 'Segregation Violations', value: health?.segregation_violations ?? 0, color: health?.segregation_violations === 0 ? 'var(--positive)' : '#ef4444' },
                { label: 'Ungrounded Blocked', value: health?.ungrounded_amounts_blocked ?? 0, color: health?.ungrounded_amounts_blocked === 0 ? 'var(--positive)' : '#f59e0b' },
                { label: 'Total Exceptions', value: fmtN(health?.total_exceptions_processed ?? 0) },
                { label: 'Total Resolved', value: fmtN(health?.total_resolved ?? 0), color: 'var(--positive)' },
              ]} />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 8: Feedback Loop */}
      {activeTab === 'Feedback Loop' && (
        <Card>
          <SectionTitle>Resolution Feedback Loop</SectionTitle>
          {outcomes?.status === 'no_data' ? (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-dim)', fontSize: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 8 }}>No data yet</div>
              <div>{outcomes?.message || 'Outcomes populate from human review actions'}</div>
            </div>
          ) : (
            <>
              <KpiGrid columns={3} items={[
                { label: 'Learning Data Points', value: fmtN(outcomes?.learning_data_points ?? 0), color: 'var(--navy-800)' },
                { label: 'Acceptance Rate', value: outcomes?.acceptance_rate != null ? `${((outcomes?.acceptance_rate ?? 0) * 100).toFixed(1)}%` : 'No data yet', color: 'var(--positive)' },
                { label: 'Escalated', value: fmtN(outcomes?.escalated ?? 0), color: '#f59e0b' },
              ]} />
              <KpiGrid columns={4} items={[
                { label: 'Accepted', value: fmtN(outcomes?.accepted ?? 0), color: 'var(--positive)' },
                { label: 'Modified', value: fmtN(outcomes?.modified ?? 0), color: 'var(--warning)' },
                { label: 'Rejected', value: fmtN(outcomes?.rejected ?? 0), color: 'var(--negative)' },
                { label: 'Escalated', value: fmtN(outcomes?.escalated ?? 0), color: 'var(--navy-700)' },
              ]} />
            </>
          )}
        </Card>
      )}

      {/* TAB 9: Confidence Calibration */}
      {activeTab === 'Confidence Calibration' && (
        <Card>
          <SectionTitle>Confidence Calibration</SectionTitle>
          {calibration?.calibrations && calibration.calibrations.length > 0 && calibration.status !== 'no_data' ? (
            <DataTable columns={calibrationCols} rows={calibration.calibrations} />
          ) : (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 8 }}>Awaiting human review data</div>
              <div>Calibration data will populate as humans approve, modify, or reject AI resolutions.</div>
            </div>
          )}
        </Card>
      )}

      {/* TAB 10: Auto-Approval Engine */}
      {activeTab === 'Auto-Approval Engine' && autoApproval && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <SectionTitle>Auto-Approval Engine (7-Layer)</SectionTitle>
            {autoApproval.provisional_pending > 0 && (
              <ActionButton label={`${autoApproval.provisional_pending} Provisional JEs`} onClick={() => navigate('/control-center')} variant="secondary" size="sm" />
            )}
          </div>
          <KpiGrid columns={4} items={[
            { label: 'Total Processed', value: fmtN(autoApproval.total_processed) },
            { label: 'Auto-Approved', value: fmtN(autoApproval.approved), color: 'var(--positive)' },
            { label: 'Blocked', value: fmtN(autoApproval.blocked + autoApproval.routed_to_human), color: '#f59e0b' },
            { label: 'Provisional Pending', value: fmtN(autoApproval.provisional_pending), color: 'var(--navy-600)' },
          ]} />
          {Object.keys(autoApproval.blocked_by_layer).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 10, textTransform: 'uppercase' }}>Blocked by Layer</div>
              {[1, 2, 3, 4, 5, 6, 7].map(layer => {
                const count = autoApproval.blocked_by_layer[String(layer)] || 0;
                const label = autoApproval.layer_labels[String(layer)] || `Layer ${layer}`;
                const maxCount = Math.max(...Object.values(autoApproval.blocked_by_layer), 1);
                const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
                return (
                  <div key={layer} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 120, fontSize: 11, color: 'var(--text-dim)', textAlign: 'right' }}>L{layer}: {label}</div>
                    <div style={{ flex: 1, height: 18, background: 'rgba(0,0,0,0.04)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${barWidth}%`, height: '100%', background: count > 0 ? 'rgba(245,158,11,0.4)' : 'transparent', borderRadius: 3 }} />
                    </div>
                    <div style={{ width: 40, fontSize: 11, fontWeight: 600, color: count > 0 ? 'var(--warning)' : 'var(--text-dim)', textAlign: 'right' }}>{count}</div>
                  </div>
                );
              })}
            </div>
          )}
          {(autoApproval.drift_alerts || []).length > 0 && (
            <div style={{ padding: '12px 14px', background: 'var(--negative-bg)', borderRadius: 8, border: '1px solid var(--negative-border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--negative)', marginBottom: 8 }}>Cumulative Drift Alerts</div>
              {(autoApproval.drift_alerts || []).filter((d: any, i: number, arr: any[]) => arr.findIndex((x: any) => x.account_family === d.account_family) === i).map((d: any) => (
                <div key={`drift-${d.account_family}`} style={{ fontSize: 11, color: 'var(--negative)', marginBottom: 4 }}>
                  Account family {d.account_family}: cumulative adjustment ${d.cumulative_adjustment?.toLocaleString() ?? 0} exceeds {d.threshold ?? 0}% of TB total ${d.tb_total?.toLocaleString() ?? 0} ({(d.pct ?? 0).toFixed(1)}%)
                </div>
              ))}
            </div>
          )}
          {autoApproval.total_processed === 0 && (
            <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>No exceptions processed by the auto-approval engine yet.</div>
          )}
        </Card>
      )}

      {activeTab === 'Agent Learning' && (
        <AgentLearningTab />
      )}
    </PageLayout>
  );
}

// -- Style helpers --
function levelColor(level: string): string {
  const map: Record<string, string> = { L1: 'var(--positive)', L2: 'var(--navy-600)', L3: 'var(--warning)', L4: '#ea580c', L5: 'var(--negative)', L6: 'var(--navy-800)' };
  return map[level] || '#fff';
}

const safetyChecks = [
  { key: 'grounding', label: 'Amount Grounding Enforced', description: 'JE amounts must trace to source ERP data -- no invented numbers' },
  { key: 'no_invented', label: 'No Invented Rates/Percentages', description: 'AI cannot fabricate tax rates, discount percentages, or exchange rates' },
  { key: 'balance', label: 'Balance Validation Gate Active', description: 'Every journal entry must balance (debits = credits) before posting' },
  { key: 'critical_block', label: 'CRITICAL Findings Block Posting', description: 'Any CRITICAL audit finding prevents auto-resolution' },
  { key: 'audit_trail', label: 'Full Audit Trail on Every Action', description: 'SOX 404-compliant immutable audit trail for all decisions' },
  { key: 'sod', label: 'Segregation of Duties Enforced', description: 'The AI that detects cannot approve -- independent check required' },
];

function getCheckStatus(key: string, health: HealthData | null): boolean {
  if (!health) return false;
  switch (key) {
    case 'grounding': return health.ungrounded_amounts_blocked === 0 || health.critical_findings === 0;
    case 'no_invented': return health.critical_findings === 0;
    case 'balance': return (health.balance_validation?.rejection_rate ?? 0) === 0;
    case 'critical_block': return true;
    case 'audit_trail': return health.total_audit_trail_entries > 0;
    case 'sod': return health.segregation_violations === 0;
    default: return true;
  }
}
